import React from "react";
import { Reveal } from "./Reveal";
import { LionMark } from "./Logo";
import { useLang } from "../i18n";

const isSoon = (s) => /\((Coming Soon|În curând)\)/i.test(s);

function Branch({ sector, name, items, fill }) {
  return (
    <div
      className={`group flex flex-col border border-gold/15 bg-ink/80 p-4 backdrop-blur-xl transition-all duration-500 hover:border-gold/40 hover:bg-ink-elevated sm:p-5 ${
        fill ? "h-full" : ""
      }`}
    >
      <span className="overline text-[10px]">{sector}</span>
      <h4 className="mt-1.5 font-serif text-lg leading-tight text-gold-leaf transition-colors group-hover:text-gold-light">
        {name}
      </h4>
      <div className="mt-3 h-px w-full bg-gold/10" />
      <ul className="mt-3 space-y-1.5">
        {items.map((it) => {
          const soon = isSoon(it);
          return (
            <li key={it} className="flex items-start gap-2">
              <span
                className={`mt-[6px] h-1 w-1 flex-shrink-0 rotate-45 ${soon ? "bg-gold/40" : "bg-gold"}`}
              />
              <span
                className={`text-[11px] leading-snug transition-colors ${
                  soon ? "italic text-white/40" : "text-white/70 group-hover:text-white/90"
                }`}
              >
                {it}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// Horizontal bus segment, trimmed at the first/last node so the ends stop
// exactly at the outer stubs (clean L-corners, no overshoot).
function busEdges(i, n) {
  if (i === 0) return "left-1/2 right-0";
  if (i === n - 1) return "left-0 right-1/2";
  return "left-0 right-0";
}

export default function Portfolio() {
  const { t } = useLang();
  const { subgroups, divisions } = t.portfolio;

  return (
    <section id="portfolio" className="section-pad relative bg-ink-surface">
      <div className="container-luxe">
        <Reveal className="mb-14 text-center">
          <p className="overline mb-4">{t.portfolio.overline}</p>
          <h2 className="mx-auto max-w-3xl font-serif text-3xl font-light text-white sm:text-4xl lg:text-5xl">
            {t.portfolio.title}
          </h2>
        </Reveal>

        {/* ── Org chart ───────────────────────────────────────────────── */}
        <div className="flex w-full flex-col items-center">
          {/* Top tier — the 3 sub-groups (bus runs BELOW the cards, down to the hub) */}
          <div className="w-full">
            {/* desktop: connected tree */}
            <ul className="hidden w-full items-stretch md:flex">
              {subgroups.map((g, i) => (
                <li key={g.name} className="relative flex flex-1 flex-col px-2.5 pb-8">
                  <Branch sector={g.sector} name={g.name} items={g.items} fill />
                  <span className={`absolute bottom-0 h-px bg-gold/30 ${busEdges(i, subgroups.length)}`} />
                  <span className="absolute bottom-0 left-1/2 h-8 w-px -translate-x-1/2 bg-gold/30" />
                </li>
              ))}
            </ul>
            {/* mobile: stacked */}
            <div className="grid grid-cols-1 gap-5 md:hidden">
              {subgroups.map((g) => (
                <Reveal key={g.name}>
                  <Branch sector={g.sector} name={g.name} items={g.items} />
                </Reveal>
              ))}
            </div>
          </div>

          {/* Spine with the sub-groups label sitting on the line */}
          <div className="relative h-16 w-px bg-gold/30 lg:h-20">
            <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 whitespace-nowrap rounded-full border border-gold/25 bg-ink-surface px-4 py-1 text-[10px] uppercase tracking-[0.28em] text-gold/70">
              {t.portfolio.subLabel}
            </span>
          </div>

          {/* Root node */}
          <Reveal>
            <div className="relative z-10">
              <div className="pointer-events-none absolute -inset-6 rounded-full bg-gold/10 blur-2xl" />
              <div className="relative flex items-center gap-3 border border-gold/30 bg-ink-elevated px-5 py-3.5 shadow-[0_8px_40px_rgba(212,175,55,0.12)] sm:gap-4 sm:px-7 sm:py-4">
                <LionMark size={40} />
                <div className="text-left">
                  <span className="block font-serif text-lg tracking-[0.14em] text-gold-leaf sm:text-xl">
                    CHESPI GROUP
                  </span>
                  <span className="block text-[9px] uppercase tracking-[0.3em] text-white/45 sm:text-[10px]">
                    Est. 1995 · Constanța
                  </span>
                </div>
              </div>
            </div>
          </Reveal>

          {/* Spine with the divisions label sitting on the line */}
          <div className="relative h-16 w-px bg-gold/30 lg:h-20">
            <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 whitespace-nowrap rounded-full border border-gold/25 bg-ink-surface px-4 py-1 text-[10px] uppercase tracking-[0.28em] text-gold/70">
              {t.portfolio.moreTitle}
            </span>
          </div>

          {/* Bottom tier — divisions (bus runs ABOVE the cards) */}
          <div className="w-full">
            {/* desktop: connected tree */}
            <ul className="hidden w-full items-start lg:flex">
              {divisions.map((g, i) => (
                <li key={g.name} className="relative flex flex-1 flex-col px-2.5 pt-8">
                  <span className={`absolute top-0 h-px bg-gold/30 ${busEdges(i, divisions.length)}`} />
                  <span className="absolute top-0 left-1/2 h-8 w-px -translate-x-1/2 bg-gold/30" />
                  <Branch sector={g.sector} name={g.name} items={g.items} />
                </li>
              ))}
            </ul>
            {/* mobile / tablet: even-height grid */}
            <div className="grid auto-rows-fr grid-cols-2 gap-4 sm:gap-5 lg:hidden">
              {divisions.map((g) => (
                <Reveal key={g.name} className="h-full">
                  <Branch sector={g.sector} name={g.name} items={g.items} fill />
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
